/**
 * M01 — Entrar (F1.8, porta de entrada do painel do Admin).
 *
 * O QUE ESTA TELA RESOLVE
 * Uma pessoa precisa provar quem é para entrar. Só isso. Tudo o mais que aparece aqui existe
 * porque, quando entrar falha, o funcionário fica parado no pátio sem saber se o problema é
 * dele, do celular ou do sistema — e liga para o Frederico. Cada frase desta tela existe para
 * evitar esse telefonema: diz o que aconteceu, o que fazer agora e o que acontece com o que
 * ele já fez.
 *
 * AÇÃO PRINCIPAL (docs/technical/03 §3.5, tabela B, linha M01): **Entrar**.
 * Secundárias: "Esqueci minha senha" e "Informar problema" (M20). Só uma delas tem peso visual
 * em cada passo — é a regra (i) do §3.5: uma tela nunca tem dois botões de mesmo peso.
 *
 * A REGRA QUE MANDA NESTE ARQUIVO
 *   NINGUÉM PODE FICAR PRESO DO LADO DE FORA SEM CAMINHO DE SAÍDA.
 * Por isso, quando a política da empresa exige segundo fator e a pessoa ainda não cadastrou
 * nenhum, esta tela **não** manda "procure o administrador": ela mostra o QR code ali mesmo e
 * termina o cadastro. Bloquear alguém exigindo uma coisa que ele não tem como fazer sozinho é
 * o mesmo que demitir o técnico às 6 da manhã na porta do cliente.
 *
 * O QUE ESTA TELA NÃO FAZ (e por quê)
 *  - **Não usa `porQueNaoPode()` de `auth/sessao.ts`.** Aquela função responde "qual capacidade
 *    está faltando na sua função", e capacidade só existe depois que se sabe quem é a pessoa —
 *    o que, aqui, por definição, ainda não se sabe. O contrato que ela carrega (botão nunca some
 *    em silêncio; a frase do porquê fica visível) é cumprido do mesmo jeito, por
 *    `porQueNaoPodeEntrar()` logo abaixo, que devolve frase ou `null` exatamente como ela.
 *  - **Não registra o acesso** em `public.acesso`. A RPC `registrar_acesso` é revogada de
 *    `authenticated` de propósito (migration 2026081903 §7.6): quem grava IP e user agent é a
 *    Edge, porque o cliente pode mentir os dois.
 *  - **Não decide se a pessoa é Admin.** Entrar é entrar; o que ela pode fazer lá dentro é o
 *    servidor que diz, em cada RPC e em cada policy.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import { Botao } from '../componentes/Botao.js';
import { Campo } from '../componentes/Campo.js';
import { EstadoDaTela } from '../componentes/EstadoDaTela.js';
import { sair as sairDaSessao } from '../auth/sessao.js';
import { nivelDeGarantia, supabase } from '../lib/supabase.js';
import { ESTADOS, type EstadoTela } from '../estados/tipos.js';

// =====================================================================================
// 1. Contrato de estados desta tela (docs/technical/03 §3.5, tabela B, linha M01)
// =====================================================================================
// A linha da tabela B para M01 é:
//   1 carregando ● · 2 vazio — · 3 sem internet ◐ · 4 Tiny — · 5 sem permissão ◐ ·
//   6 versão incompatível ● · 7 ação em andamento ● · 8 falha recuperável ● ·
//   9 conflito — · 10 sucesso parcial — · 11 sucesso completo —
// Declarar isto como dado (e não como comentário solto) deixa o teste conferir que a tela não
// esqueceu um estado que a tabela manda tratar nem inventou um que a tabela nega.
export type AplicacaoDeEstado = 'padrao' | 'especifico' | 'nao_se_aplica';

export const ESTADOS_DA_TELA: Readonly<
  Record<EstadoTela, { aplica: AplicacaoDeEstado; nota: string }>
> = {
  carregando: {
    aplica: 'padrao',
    nota: 'Enquanto perguntamos ao aparelho se já existe uma sessão guardada aqui.',
  },
  vazio: {
    aplica: 'nao_se_aplica',
    nota: 'Não há lista nem cadastro nesta tela — não existe "nada por aqui ainda".',
  },
  sem_internet: {
    aplica: 'especifico',
    // Texto: o PADRÃO, palavra por palavra — quem já viu esta frase no celular do pátio precisa
    // reconhecê-la aqui. Ação: específica, porque "Ver o que está só aqui" (M19) fica atrás do
    // login e ninguém consegue abrir sem entrar. A saída honesta é continuar com a sessão que já
    // está guardada neste aparelho; entrar com e-mail e senha é o único caminho que exige rede.
    nota: 'Texto padrão; a ação vira "Continuar com a sessão salva", porque a M19 fica atrás do login.',
  },
  tiny_indisponivel: {
    aplica: 'nao_se_aplica',
    nota: 'Entrar não passa pelo Tiny em nenhum ponto.',
  },
  sem_permissao: {
    aplica: 'especifico',
    // A frase padrão ("peça a um gestor") está certa, mas incompleta na hora do bloqueio: quem é
    // bloqueado com trabalho guardado no celular precisa saber que aquilo não foi apagado, senão
    // some evidência de serviço que não existe em nenhum outro lugar.
    nota: 'Conta bloqueada ou sem cadastro na empresa: a frase diz também o que acontece com o que ficou guardado no aparelho.',
  },
  versao_incompativel: {
    aplica: 'padrao',
    nota: 'Aplicativo abaixo da versão mínima: entrar de novo fica bloqueado até atualizar.',
  },
  acao_em_andamento: {
    aplica: 'especifico',
    // O padrão é "Fazendo…". Num botão de login isso não diz nada; "Entrando…" diz qual ação
    // está em curso, que é a informação que a pessoa precisa para não apertar de novo.
    nota: 'O botão mostra "Entrando…" / "Conferindo…" em vez de "Fazendo…", para dizer qual ação está em curso.',
  },
  falha_recuperavel: {
    aplica: 'especifico',
    // A própria tabela A manda: "Não deu certo agora: <motivo em português>". O motivo é o que
    // muda tudo aqui — "senha errada" e "servidor fora do ar" pedem reações opostas.
    nota: 'O motivo concreto entra no texto (senha errada, muitas tentativas, servidor fora do ar).',
  },
  conflito: {
    aplica: 'nao_se_aplica',
    nota: 'Não há campo preenchido a quatro mãos aqui; quem decide é o servidor, na hora.',
  },
  sucesso_parcial: {
    aplica: 'nao_se_aplica',
    nota: 'Entrar não tem fila local: ou entrou, ou não entrou.',
  },
  sucesso_completo: {
    aplica: 'nao_se_aplica',
    nota: 'O sucesso desta tela é a próxima tela abrir; parabenizar a pessoa por ter entrado seria ruído.',
  },
};

// =====================================================================================
// 2. Texto para gente — nada de código HTTP, nada de nome de biblioteca
// =====================================================================================
export const TEXTO_SENHA_ERRADA = 'E-mail ou senha não conferem. Confira e tente de novo.';

export const TEXTO_CONTA_BLOQUEADA =
  'Sua conta está bloqueada — peça a um gestor para liberar. O que você fez e ainda não subiu continua guardado neste aparelho; quem cuida do sistema consegue pedir a devolução.';

export const TEXTO_SEM_CADASTRO =
  'O e-mail e a senha estão certos, mas você ainda não faz parte de nenhuma empresa aqui — peça a um gestor para incluir você.';

export const TEXTO_SEM_REDE = 'Sem internet — não deu para falar com o servidor. Nada mudou.';

export const TEXTO_MUITAS_TENTATIVAS =
  'Muitas tentativas seguidas. Espere um minuto e tente de novo.';

export const TEXTO_EMAIL_NAO_CONFIRMADO =
  'Este e-mail ainda não foi confirmado. Procure a mensagem de confirmação na sua caixa de entrada.';

export const TEXTO_CODIGO_ERRADO =
  'O código não confere. Ele troca a cada 30 segundos — veja o número que está aparecendo agora no aplicativo autenticador.';

export const TEXTO_CODIGO_EXPIROU =
  'Esse código demorou demais e perdeu a validade. Veja o número atual no aplicativo autenticador e digite de novo.';

export const TEXTO_FALHA_GENERICA = 'Não foi possível entrar agora — vamos tentar de novo.';

export const TEXTO_PRECISA_DE_REDE =
  'Entrar com e-mail e senha precisa de internet. O que já está guardado neste aparelho continua lá.';

export const TEXTO_PRECISA_ATUALIZAR =
  'Atualize o aplicativo para entrar. O que já está guardado neste aparelho continua lá e sobe sozinho quando o aplicativo for atualizado.';

export const TEXTO_FALTA_EMAIL_E_SENHA = 'Escreva o seu e-mail e a sua senha para entrar.';
export const TEXTO_FALTA_EMAIL = 'Escreva o seu e-mail para entrar.';
export const TEXTO_FALTA_SENHA = 'Escreva a sua senha para entrar.';
export const TEXTO_FALTA_CODIGO = 'Digite os 6 números que aparecem no aplicativo autenticador.';

export const TEXTO_RECUPERACAO_ENVIADA =
  'Se este e-mail estiver cadastrado, você vai receber uma mensagem com o caminho para criar uma senha nova.';

export const TEXTO_FALTA_EMAIL_RECUPERACAO =
  'Escreva o seu e-mail acima para receber o caminho de senha nova.';

/** De onde vem o código: dito por extenso, porque metade das pessoas não sabe o que é "TOTP". */
export const TEXTO_DE_ONDE_VEM_O_CODIGO =
  'O código está no aplicativo autenticador do seu celular (Google Authenticator, Microsoft Authenticator, 1Password e parecidos). São 6 números que trocam sozinhos a cada 30 segundos — digite o que estiver aparecendo agora.';

export const TEXTO_COMO_CADASTRAR =
  'A sua empresa exige um segundo fator para entrar, e você ainda não cadastrou o seu. Faça agora: abra o aplicativo autenticador do seu celular (Google Authenticator, Microsoft Authenticator, 1Password e parecidos), aponte a câmera para o quadrado abaixo e depois digite os 6 números que ele mostrar.';

export const TEXTO_SEGREDO_MANUAL =
  'Não consegue apontar a câmera? Escolha "digitar chave" no aplicativo autenticador e escreva este código:';

/**
 * Frase padrão de "sem internet" — a MESMA do resto do produto, de propósito (`ESTADOS`).
 * Repetimos por referência e não por cópia para que trocar a frase em um lugar troque em todos.
 */
export const TEXTO_SEM_INTERNET = ESTADOS.sem_internet.texto;

const PADROES_DE_REDE =
  /failed to fetch|networkerror|network request failed|load failed|fetch failed/i;

/**
 * Erro técnico → frase de operação. É aqui que "401", "AuthApiError" e "Invalid login
 * credentials" morrem: nada disso pode chegar à tela (§3.5 regra ii).
 */
export function mensagemDeErro(erro: unknown): string {
  const bruto = (erro ?? {}) as {
    code?: unknown;
    status?: unknown;
    message?: unknown;
    name?: unknown;
  };
  const codigo = typeof bruto.code === 'string' ? bruto.code : '';
  const texto = typeof bruto.message === 'string' ? bruto.message : '';
  const status = typeof bruto.status === 'number' ? bruto.status : 0;
  const nome = typeof bruto.name === 'string' ? bruto.name : '';

  // Rede primeiro: sem rede, nenhum outro diagnóstico vale — o servidor nem foi consultado.
  if (nome === 'AuthRetryableFetchError' || PADROES_DE_REDE.test(texto)) return TEXTO_SEM_REDE;

  switch (codigo) {
    case 'invalid_credentials':
      return TEXTO_SENHA_ERRADA;
    case 'user_banned':
      return TEXTO_CONTA_BLOQUEADA;
    case 'email_not_confirmed':
      return TEXTO_EMAIL_NAO_CONFIRMADO;
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
      return TEXTO_MUITAS_TENTATIVAS;
    case 'mfa_verification_failed':
      return TEXTO_CODIGO_ERRADO;
    case 'mfa_challenge_expired':
      return TEXTO_CODIGO_EXPIROU;
    // P0001 é o código que TODAS as nossas funções de banco usam para falar com a pessoa: a
    // frase já sai do servidor em português de operação, então é repassada inteira.
    case 'P0001':
      return texto.trim() !== '' ? texto : TEXTO_FALHA_GENERICA;
    case '42501':
    case 'PGRST301':
      return ESTADOS.sem_permissao.texto;
    default:
      break;
  }

  if (status === 429) return TEXTO_MUITAS_TENTATIVAS;
  // Versões antigas do servidor de login respondem sem `code`; a frase em inglês é o que sobra.
  if (/invalid login credentials/i.test(texto)) return TEXTO_SENHA_ERRADA;
  if (/invalid totp code|invalid mfa|invalid code/i.test(texto)) return TEXTO_CODIGO_ERRADO;
  return TEXTO_FALHA_GENERICA;
}

/**
 * Contraparte de `porQueNaoPode()` (auth/sessao.ts) para a única tela onde não existe sessão.
 * Mesmo contrato: devolve a frase que explica, ou `null` quando a pessoa pode agir. Quem usa a
 * frase é o `Botao`, que fica visível e indisponível — nunca some.
 */
export function porQueNaoPodeEntrar(entrada: {
  email: string;
  senha: string;
  online: boolean;
  versaoIncompativel: boolean;
}): string | null {
  if (entrada.versaoIncompativel) return TEXTO_PRECISA_ATUALIZAR;
  if (!entrada.online) return TEXTO_PRECISA_DE_REDE;
  const semEmail = entrada.email.trim() === '';
  const semSenha = entrada.senha === '';
  if (semEmail && semSenha) return TEXTO_FALTA_EMAIL_E_SENHA;
  if (semEmail) return TEXTO_FALTA_EMAIL;
  if (semSenha) return TEXTO_FALTA_SENHA;
  return null;
}

/** Só os 6 números interessam; espaço e traço que o autenticador mostra são ruído do jeito de ler. */
export function limparCodigo(bruto: string): string {
  return bruto.replace(/\D/g, '').slice(0, 6);
}

export function porQueNaoPodeConferir(codigo: string): string | null {
  return limparCodigo(codigo).length === 6 ? null : TEXTO_FALTA_CODIGO;
}

// =====================================================================================
// 3. Porta de dados
// =====================================================================================
/**
 * Existe para que o teste prove o COMPORTAMENTO (qual chamada, com qual dado, em qual ordem)
 * sem imitar o cliente do Supabase — imitação que quebra a cada atualização da biblioteca e não
 * prova nada sobre a regra de produto.
 */
export interface FatorTotp {
  id: string;
  verificado: boolean;
}

export interface PerfilDeEntrada {
  /** `false` = autenticou, mas não existe linha em `usuario`: não faz parte de nenhuma empresa. */
  encontrado: boolean;
  bloqueado: boolean;
  /** `usuario.politica_2fa = 'obrigatorio'` — a empresa exige segundo fator desta pessoa. */
  exige2fa: boolean;
}

export interface PortaEntrar {
  /** Já existe token guardado neste aparelho? É o que permite continuar sem internet. */
  sessaoSalva(): Promise<boolean>;
  entrarComSenha(entrada: { email: string; senha: string }): Promise<void>;
  perfil(): Promise<PerfilDeEntrada>;
  nivelAtual(): Promise<'aal1' | 'aal2' | 'nenhum'>;
  fatoresTotp(): Promise<FatorTotp[]>;
  desafiar(fatorId: string): Promise<{ desafioId: string }>;
  conferirCodigo(entrada: { fatorId: string; desafioId: string; codigo: string }): Promise<void>;
  cadastrarSegundoFator(): Promise<{ fatorId: string; qrCode: string; segredo: string }>;
  enviarRecuperacaoDeSenha(email: string): Promise<void>;
  sair(): Promise<void>;
}

export function portaSupabase(): PortaEntrar {
  return {
    async sessaoSalva() {
      // `getSession` lê do armazenamento do aparelho e não vai à rede: é exatamente a pergunta
      // "esta pessoa já entrou aqui antes?", que precisa ter resposta mesmo offline.
      const { data } = await supabase().auth.getSession();
      return data.session !== null;
    },

    async entrarComSenha({ email, senha }) {
      const { error } = await supabase().auth.signInWithPassword({ email, password: senha });
      if (error) throw error;
    },

    async perfil() {
      // `meu_perfil` e não a tabela `usuario`: a RPC é quem enxerga bloqueio e política de 2FA
      // (o GRANT de coluna esconde os dois na tabela). Ela funciona já no primeiro fator, que é
      // o que permite avisar do bloqueio ANTES de pedir o código de 6 dígitos.
      const { data, error } = await supabase().rpc('meu_perfil');
      if (error) throw error;
      const linha = (Array.isArray(data) ? data[0] : data) as
        { politica_2fa?: string | null; bloqueado?: boolean | null } | null | undefined;
      if (!linha) return { encontrado: false, bloqueado: false, exige2fa: false };
      return {
        encontrado: true,
        bloqueado: linha.bloqueado === true,
        exige2fa: linha.politica_2fa === 'obrigatorio',
      };
    },

    nivelAtual() {
      return nivelDeGarantia();
    },

    async fatoresTotp() {
      const { data, error } = await supabase().auth.mfa.listFactors();
      if (error) throw error;
      return (data?.all ?? [])
        .filter((fator) => fator.factor_type === 'totp')
        .map((fator) => ({ id: fator.id, verificado: fator.status === 'verified' }));
    },

    async desafiar(fatorId) {
      const { data, error } = await supabase().auth.mfa.challenge({ factorId: fatorId });
      if (error) throw error;
      return { desafioId: data.id };
    },

    async conferirCodigo({ fatorId, desafioId, codigo }) {
      const { error } = await supabase().auth.mfa.verify({
        factorId: fatorId,
        challengeId: desafioId,
        code: codigo,
      });
      if (error) throw error;
    },

    async cadastrarSegundoFator() {
      const cliente = supabase();
      // Limpa cadastro que ficou pela metade (a pessoa desistiu no meio, o navegador fechou):
      // sem isto, cada nova tentativa empilha um fator não confirmado e a segunda tentativa
      // falha por nome repetido — a pessoa ficaria presa do lado de fora por causa de lixo.
      const { data: lista } = await cliente.auth.mfa.listFactors();
      for (const fator of lista?.all ?? []) {
        if (fator.factor_type === 'totp' && fator.status !== 'verified') {
          await cliente.auth.mfa.unenroll({ factorId: fator.id });
        }
      }
      const { data, error } = await cliente.auth.mfa.enroll({ factorType: 'totp' });
      if (error) throw error;
      return { fatorId: data.id, qrCode: data.totp.qr_code, segredo: data.totp.secret };
    },

    async enviarRecuperacaoDeSenha(email) {
      const { error } = await supabase().auth.resetPasswordForEmail(email);
      if (error) throw error;
    },

    async sair() {
      await sairDaSessao();
    },
  };
}

// =====================================================================================
// 4. Peças visuais (só tokens; nenhum valor literal de cor ou tamanho)
// =====================================================================================
const ESTILO_PAGINA: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--esp-5)',
  background: 'var(--cor-fundo)',
};

const ESTILO_CARTAO: CSSProperties = {
  width: '100%',
  maxWidth: 'var(--largura-celular)',
  background: 'var(--cor-superficie)',
  border: '1px solid var(--cor-linha)',
  borderRadius: 'var(--raio)',
  boxShadow: 'var(--sombra-md)',
  padding: 'var(--esp-6)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--esp-4)',
};

const ESTILO_EYEBROW: CSSProperties = {
  margin: 0,
  color: 'var(--cor-texto-suave)',
  fontSize: 'var(--font-size-legenda)',
  fontWeight: 'var(--peso-forte)',
  letterSpacing: 'var(--letra-eyebrow)',
  textTransform: 'uppercase',
};

const ESTILO_TITULO: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xl)',
  lineHeight: 'var(--altura-linha-compacta)',
  letterSpacing: 'var(--letra-titulo)',
};

const ESTILO_PARAGRAFO: CSSProperties = {
  margin: 0,
  color: 'var(--cor-texto-suave)',
  fontSize: 'var(--font-size-formulario)',
  lineHeight: 'var(--altura-linha-texto)',
};

/**
 * O QR precisa caber na tela e continuar legível pela câmera. Tamanho derivado da escala de
 * espaçamento (5 × 40 px = 200 px) para não cravar número solto no código.
 */
const ESTILO_QR: CSSProperties = {
  width: 'calc(var(--esp-10) * 5)',
  height: 'calc(var(--esp-10) * 5)',
  maxWidth: '100%',
  alignSelf: 'center',
  background: 'var(--cor-superficie)',
  border: '1px solid var(--cor-linha)',
  borderRadius: 'var(--raio-sm)',
  padding: 'var(--esp-2)',
};

const ESTILO_SEGREDO: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--fonte-mono)',
  fontSize: 'var(--font-size-formulario)',
  letterSpacing: 'var(--letra-eyebrow)',
  wordBreak: 'break-all',
  background: 'var(--cor-superficie-3)',
  border: '1px solid var(--cor-linha)',
  borderRadius: 'var(--raio-sm)',
  padding: 'var(--esp-2) var(--esp-3)',
  color: 'var(--cor-texto)',
};

const ESTILO_RODAPE: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--esp-2)',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderTop: '1px solid var(--cor-linha)',
  paddingTop: 'var(--esp-3)',
};

// =====================================================================================
// 5. A tela
// =====================================================================================
type Passo =
  | { nome: 'verificando' }
  | { nome: 'credenciais' }
  | { nome: 'codigo'; fatorId: string; desafioId: string }
  | { nome: 'cadastro'; fatorId: string; qrCode: string; segredo: string }
  | { nome: 'barrado'; mensagem: string };

export interface EntrarProps {
  /** Chamado quando a sessão está pronta para uso — inclusive já com o segundo fator, quando ele é exigido. */
  onEntrou: () => void;
  /** Injeção de dependência para teste. Sem isto, fala com o Supabase de verdade. */
  porta?: PortaEntrar | undefined;
  /** Vem do `bootstrap` (app_release.min_supported): aplicativo velho demais para entrar. */
  versaoIncompativel?: boolean | undefined;
  aoAtualizarApp?: (() => void) | undefined;
  /** Regra (iii) do §3.5: toda falha recuperável oferece o caminho da M20. */
  aoInformarProblema?: (() => void) | undefined;
  /** Conexão controlada (útil em teste). Sem isto, a tela escuta o próprio navegador. */
  online?: boolean | undefined;
}

export function Entrar({
  onEntrou,
  porta,
  versaoIncompativel = false,
  aoAtualizarApp,
  aoInformarProblema,
  online,
}: EntrarProps) {
  const portaAtual = useMemo(() => porta ?? portaSupabase(), [porta]);

  const [passo, setPasso] = useState<Passo>({ nome: 'verificando' });
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [temSessaoSalva, setTemSessaoSalva] = useState(false);
  const [conexaoDoNavegador, setConexaoDoNavegador] = useState<boolean>(() => navigator.onLine);

  const tituloRef = useRef<HTMLHeadingElement>(null);

  const conectado = online ?? conexaoDoNavegador;

  // Perguntar ao aparelho, e não à rede, se já existe sessão guardada: é o que sustenta o
  // estado "sem internet" desta tela — quem já entrou antes continua entrando.
  useEffect(() => {
    let vivo = true;
    void portaAtual
      .sessaoSalva()
      .then((tem) => {
        if (!vivo) return;
        setTemSessaoSalva(tem);
        setPasso({ nome: 'credenciais' });
      })
      .catch(() => {
        if (!vivo) return;
        setTemSessaoSalva(false);
        setPasso({ nome: 'credenciais' });
      });
    return () => {
      vivo = false;
    };
  }, [portaAtual]);

  useEffect(() => {
    const conectou = () => setConexaoDoNavegador(true);
    const caiu = () => setConexaoDoNavegador(false);
    window.addEventListener('online', conectou);
    window.addEventListener('offline', caiu);
    return () => {
      window.removeEventListener('online', conectou);
      window.removeEventListener('offline', caiu);
    };
  }, []);

  // Quando o passo muda, quem navega por teclado ou leitor de tela precisa ser levado ao novo
  // título — senão o foco fica num botão que sumiu e a pessoa não descobre o que apareceu.
  // Não fazemos isso na abertura da tela (verificando → credenciais): roubar foco de quem
  // acabou de abrir a página é grosseria, não acessibilidade.
  useEffect(() => {
    if (passo.nome === 'verificando' || passo.nome === 'credenciais') return;
    tituloRef.current?.focus();
  }, [passo.nome]);

  /**
   * O que fazer depois que e-mail e senha passaram. A ordem importa:
   *  1. bloqueio primeiro — pedir o código de 6 dígitos a quem já está bloqueado é crueldade;
   *  2. já está no segundo fator? entra;
   *  3. tem fator confirmado? pede o código;
   *  4. a empresa exige e ele não tem? **cadastra aqui mesmo** — nunca deixar sem saída;
   *  5. nada disso? entra.
   */
  const seguirDepoisDoLogin = useCallback(async () => {
    const perfil = await portaAtual.perfil();

    if (!perfil.encontrado) {
      await portaAtual.sair();
      setPasso({ nome: 'barrado', mensagem: TEXTO_SEM_CADASTRO });
      return;
    }
    if (perfil.bloqueado) {
      // Sair de propósito: deixar meia sessão de pé faria o resto do painel pensar que a pessoa
      // entrou, e cada tela lá dentro descobriria o bloqueio do seu jeito.
      await portaAtual.sair();
      setPasso({ nome: 'barrado', mensagem: TEXTO_CONTA_BLOQUEADA });
      return;
    }

    if ((await portaAtual.nivelAtual()) === 'aal2') {
      onEntrou();
      return;
    }

    const fatores = await portaAtual.fatoresTotp();
    const confirmado = fatores.find((fator) => fator.verificado);
    if (confirmado) {
      const { desafioId } = await portaAtual.desafiar(confirmado.id);
      setCodigo('');
      setPasso({ nome: 'codigo', fatorId: confirmado.id, desafioId });
      return;
    }

    if (perfil.exige2fa) {
      const cadastro = await portaAtual.cadastrarSegundoFator();
      setCodigo('');
      setPasso({
        nome: 'cadastro',
        fatorId: cadastro.fatorId,
        qrCode: cadastro.qrCode,
        segredo: cadastro.segredo,
      });
      return;
    }

    onEntrou();
  }, [onEntrou, portaAtual]);

  const impedimentoDeEntrar = porQueNaoPodeEntrar({
    email,
    senha,
    online: conectado,
    versaoIncompativel,
  });

  async function entrar() {
    if (impedimentoDeEntrar !== null) return;
    setOcupado(true);
    setFalha(null);
    setAviso(null);
    try {
      await portaAtual.entrarComSenha({ email: email.trim(), senha });
      await seguirDepoisDoLogin();
    } catch (erro) {
      setFalha(mensagemDeErro(erro));
    } finally {
      setOcupado(false);
    }
  }

  async function conferir(fatorId: string, desafioId: string) {
    if (porQueNaoPodeConferir(codigo) !== null) return;
    setOcupado(true);
    setFalha(null);
    try {
      await portaAtual.conferirCodigo({ fatorId, desafioId, codigo: limparCodigo(codigo) });
      onEntrou();
    } catch (erro) {
      setFalha(mensagemDeErro(erro));
      setCodigo('');
      // Um desafio já usado ou vencido não aceita segunda tentativa. Pedir outro na hora evita
      // que a pessoa digite o código certo três vezes e continue sendo recusada sem entender.
      try {
        const { desafioId: novo } = await portaAtual.desafiar(fatorId);
        setPasso((atual) => (atual.nome === 'codigo' ? { ...atual, desafioId: novo } : atual));
      } catch {
        // Sem rede para renovar o desafio: a frase de falha já está na tela; não há o que somar.
      }
    } finally {
      setOcupado(false);
    }
  }

  async function ativarSegundoFator(fatorId: string) {
    if (porQueNaoPodeConferir(codigo) !== null) return;
    setOcupado(true);
    setFalha(null);
    try {
      // Cadastrar e confirmar são coisas diferentes: o fator só passa a valer depois que a
      // pessoa prova que o aplicativo dela está gerando o mesmo número.
      const { desafioId } = await portaAtual.desafiar(fatorId);
      await portaAtual.conferirCodigo({ fatorId, desafioId, codigo: limparCodigo(codigo) });
      onEntrou();
    } catch (erro) {
      setFalha(mensagemDeErro(erro));
      setCodigo('');
    } finally {
      setOcupado(false);
    }
  }

  async function voltarParaCredenciais() {
    setOcupado(true);
    setFalha(null);
    setAviso(null);
    try {
      await portaAtual.sair();
    } catch {
      // Não conseguir avisar o servidor não pode prender a pessoa no meio do caminho.
    } finally {
      setSenha('');
      setCodigo('');
      setPasso({ nome: 'credenciais' });
      setOcupado(false);
    }
  }

  async function recuperarSenha() {
    const alvo = email.trim();
    if (alvo === '') {
      setAviso(TEXTO_FALTA_EMAIL_RECUPERACAO);
      return;
    }
    setOcupado(true);
    setFalha(null);
    try {
      await portaAtual.enviarRecuperacaoDeSenha(alvo);
      // Nunca dizemos se o e-mail existe: a frase é a mesma nos dois casos, senão esta tela vira
      // um jeito de descobrir quem trabalha aqui.
      setAviso(TEXTO_RECUPERACAO_ENVIADA);
    } catch (erro) {
      setFalha(mensagemDeErro(erro));
    } finally {
      setOcupado(false);
    }
  }

  const tituloDoPasso =
    passo.nome === 'codigo'
      ? 'Confirme o código'
      : passo.nome === 'cadastro'
        ? 'Cadastre o seu segundo fator'
        : passo.nome === 'barrado'
          ? 'Não deu para entrar'
          : 'Entrar';

  return (
    <div style={ESTILO_PAGINA}>
      <section aria-labelledby="titulo-entrar" style={ESTILO_CARTAO}>
        <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--esp-1)' }}>
          <p style={ESTILO_EYEBROW}>AGROBOX Checklist</p>
          <h1 id="titulo-entrar" ref={tituloRef} tabIndex={-1} style={ESTILO_TITULO}>
            {tituloDoPasso}
          </h1>
        </header>

        {/* Estado 6 — a versão do aplicativo é velha demais. Fica no topo porque explica por que
            o botão principal está indisponível logo abaixo. */}
        {versaoIncompativel ? (
          <EstadoDaTela
            estado="versao_incompativel"
            aoAgir={aoAtualizarApp ?? (() => window.location.reload())}
          />
        ) : null}

        {/* Estado 3 — texto padrão; ação específica, justificada em ESTADOS_DA_TELA.
            Sobre a regra (i) do §3.5 ("nunca dois botões de mesmo peso"): sem internet, o
            "Entrar" abaixo já está indisponível e desenhado em cinza, então "Continuar com a
            sessão salva" é a única ação em destaque na tela. */}
        {!conectado ? (
          <EstadoDaTela
            estado="sem_internet"
            especifico={
              temSessaoSalva
                ? {
                    acao: 'Continuar com a sessão salva',
                    porque:
                      'A M19 ("ver o que está só aqui") fica atrás do login e ninguém abre sem entrar.',
                  }
                : {
                    acao: null,
                    porque:
                      'Sem sessão guardada neste aparelho, não há nada que a pessoa possa fazer sem rede.',
                  }
            }
            aoAgir={temSessaoSalva ? onEntrou : undefined}
            acaoPrincipal={temSessaoSalva}
          />
        ) : null}

        {passo.nome === 'verificando' ? <EstadoDaTela estado="carregando" /> : null}

        {passo.nome === 'barrado' ? (
          // Estado 5 (◐): a conta existe, mas a pessoa não pode entrar. A frase diz o que fazer
          // e o que acontece com o que ficou guardado no aparelho.
          <>
            <EstadoDaTela
              estado="sem_permissao"
              especifico={{
                texto: passo.mensagem,
                acao: 'Tentar com outra conta',
                porque:
                  'A ação padrão é "Voltar"; aqui não há para onde voltar — a saída real é entrar com outra conta.',
              }}
              aoAgir={() => void voltarParaCredenciais()}
              mostrarDadosLocais={false}
            />
            {aoInformarProblema ? (
              <Botao variante="texto" onClick={aoInformarProblema}>
                Informar problema
              </Botao>
            ) : null}
          </>
        ) : null}

        {passo.nome === 'credenciais' ? (
          <form
            noValidate
            onSubmit={(evento) => {
              evento.preventDefault();
              void entrar();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--esp-4)' }}
          >
            <Campo
              rotulo="E-mail"
              tipo="email"
              valor={email}
              aoMudar={setEmail}
              obrigatorio
              autoComplete="username"
              ajuda="O mesmo e-mail que a empresa cadastrou para você."
            />
            <Campo
              rotulo="Senha"
              tipo="senha"
              valor={senha}
              aoMudar={setSenha}
              obrigatorio
              autoComplete="current-password"
            />

            {/* Sem `onClick`: quem dispara é o `onSubmit` do formulário, para que entrar pelo
                Enter e entrar pelo toque sigam exatamente o mesmo caminho. O `Botao` já cancela
                o envio quando está indisponível. */}
            <Botao
              type="submit"
              variante="principal"
              larguraTotal
              carregando={ocupado}
              textoCarregando="Entrando…"
              motivoDesabilitado={impedimentoDeEntrar}
            >
              Entrar
            </Botao>
          </form>
        ) : null}

        {passo.nome === 'codigo' ? (
          <form
            noValidate
            onSubmit={(evento) => {
              evento.preventDefault();
              void conferir(passo.fatorId, passo.desafioId);
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--esp-4)' }}
          >
            <p style={ESTILO_PARAGRAFO}>{TEXTO_DE_ONDE_VEM_O_CODIGO}</p>
            <Campo
              rotulo="Código de 6 números"
              tipo="texto"
              valor={codigo}
              aoMudar={(bruto) => setCodigo(limparCodigo(bruto))}
              obrigatorio
              autoComplete="one-time-code"
              placeholder="000000"
            />
            <Botao
              type="submit"
              variante="principal"
              larguraTotal
              carregando={ocupado}
              textoCarregando="Conferindo…"
              motivoDesabilitado={porQueNaoPodeConferir(codigo)}
            >
              Confirmar e entrar
            </Botao>
            <Botao variante="texto" onClick={() => void voltarParaCredenciais()}>
              Entrar com outra conta
            </Botao>
          </form>
        ) : null}

        {passo.nome === 'cadastro' ? (
          <form
            noValidate
            onSubmit={(evento) => {
              evento.preventDefault();
              void ativarSegundoFator(passo.fatorId);
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--esp-4)' }}
          >
            <p style={ESTILO_PARAGRAFO}>{TEXTO_COMO_CADASTRAR}</p>
            {/* O QR vem pronto do servidor de login, como imagem embutida. Ele é o caminho de
                saída: sem ele, quem cai na política de 2FA sem fator cadastrado fica preso. */}
            <img
              src={passo.qrCode}
              alt="Quadrado de leitura (QR code) para cadastrar o segundo fator no aplicativo autenticador"
              style={ESTILO_QR}
            />
            <p style={ESTILO_PARAGRAFO}>{TEXTO_SEGREDO_MANUAL}</p>
            <p style={ESTILO_SEGREDO}>{passo.segredo}</p>
            <Campo
              rotulo="Código de 6 números"
              tipo="texto"
              valor={codigo}
              aoMudar={(bruto) => setCodigo(limparCodigo(bruto))}
              obrigatorio
              autoComplete="one-time-code"
              placeholder="000000"
              ajuda="Digite o número que o aplicativo autenticador mostrar depois de ler o quadrado."
            />
            <Botao
              type="submit"
              variante="principal"
              larguraTotal
              carregando={ocupado}
              textoCarregando="Conferindo…"
              motivoDesabilitado={porQueNaoPodeConferir(codigo)}
            >
              Ativar e entrar
            </Botao>
            <Botao variante="texto" onClick={() => void voltarParaCredenciais()}>
              Entrar com outra conta
            </Botao>
          </form>
        ) : null}

        {/* Estado 8 — o motivo concreto, em português, com o caminho da M20 junto (regra iii). */}
        {falha !== null ? (
          <EstadoDaTela
            estado="falha_recuperavel"
            especifico={{
              texto: falha,
              acao: null,
              porque:
                'A própria tabela A manda dizer o motivo; a ação de tentar de novo é o próprio botão do formulário, logo acima.',
            }}
            aoInformarProblema={aoInformarProblema}
            mostrarDadosLocais={false}
          />
        ) : null}

        {/* Recado de recuperação de senha: informação, não erro — por isso `status`, não `alert`. */}
        {aviso !== null ? (
          <p role="status" aria-live="polite" style={ESTILO_PARAGRAFO}>
            {aviso}
          </p>
        ) : null}

        {passo.nome === 'credenciais' ? (
          <div style={ESTILO_RODAPE}>
            <Botao variante="texto" onClick={() => void recuperarSenha()}>
              Esqueci minha senha
            </Botao>
            {aoInformarProblema ? (
              <Botao variante="texto" onClick={aoInformarProblema}>
                Informar problema
              </Botao>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
